import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { addDays, format } from 'date-fns';
import { Loader2, MapPin, Moon, Package, ShieldCheck, Sun } from 'lucide-react';
import { Button } from '../components/Button';
import { PageHeader } from '../components/PageHeader';
import { usePlatform } from '../contexts/PlatformContext';
import { labReport } from '../data/biomarkers';
import { formatAED } from '../utils/pack';
export function Review() {
  const {
    packPills,
    packPrice,
    placeOrder,
    goal
  } = usePlatform();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  if (!goal) return <Navigate to="/profile" replace data-id="element-291" data-mp="2jfjc9zadpero" />;
  if (packPills.length === 0) return <Navigate to="/plan" replace data-id="element-292" data-mp="2ee3t6crrhibx" />;
  const morning = packPills.filter(p => p.timing === 'Morning');
  const evening = packPills.filter(p => p.timing === 'Evening');
  const arrival = format(addDays(new Date(), 5), 'EEE d MMM');
  const confirm = () => {
    setLoading(true);
    setTimeout(() => {
      placeOrder();
      navigate('/confirmed');
    }, 1000);
  };
  return <div data-id="element-293" data-mp="31q53ngv6w7hd">
      <PageHeader eyebrow="Review" title="Your 1-month pack" description="30 days, pre-portioned into morning and evening sachets." data-id="element-294" data-mp="31pv8zfz79c5c" />

      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_320px]" data-id="element-295" data-mp="36nwtm5c9xjix">
        <div className="space-y-6" data-id="element-296" data-mp="3aqbesw3f0vdd">
          <section aria-labelledby="calendar-heading" className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-297" data-mp="3apa26n6r40tj">
            <h2 id="calendar-heading" className="flex items-center gap-2 font-medium" data-id="element-298" data-mp="2j06vz1vb0ge9">
              <Package className="size-4 text-[#9b8cf0]" data-id="element-299" data-mp="2n21yg9oz0ztm" /> 30 daily sachets
            </h2>
            <ol className="mt-5 grid grid-cols-6 gap-2 sm:grid-cols-10" data-id="element-300" data-mp="2n0798j6o2ljq">
              {Array.from({
              length: 30
            }, (_, i) => <li key={i} className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-white/10 bg-white/[0.03]" data-id="element-301" data-mp="1vbdu6os03ebq">
                  <span className="font-mono text-[10px] text-muted-foreground" data-id="element-302" data-mp="1zacz49dqfb36">{i + 1}</span>
                  <span className="flex gap-0.5" aria-hidden="true" data-id="element-303" data-mp="32lilpw00sa80">
                    {packPills.slice(0, 4).map(p => <span key={p.id} className="size-1 rounded-full" style={{
                  backgroundColor: p.color
                }} data-id="element-304" data-mp="3cd6jsfr30pig" />)}
                  </span>
                </li>)}
            </ol>
          </section>

          <div className="grid gap-6 md:grid-cols-2" data-id="element-305" data-mp="2jnpnh70ccogy">
            {[{
            label: 'Morning sachet',
            icon: Sun,
            items: morning
          }, {
            label: 'Evening sachet',
            icon: Moon,
            items: evening
          }].map(group => <section key={group.label} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-306" data-mp="2sn2w5xgn590m">
                <h2 className="flex items-center gap-2 font-medium" data-id="element-307" data-mp="2opyie8wry0lp">
                  <group.icon className="size-4 text-[#9b8cf0]" data-id="element-308" data-mp="7xdhpljunp8g" /> {group.label}
                </h2>
                <ul className="mt-4 space-y-3" data-id="element-309" data-mp="3wtkyj36unmu">
                  {group.items.length === 0 && <li className="text-sm text-muted-foreground" data-id="element-310" data-mp="bfui8mnebl1r">Nothing in this sachet.</li>}
                  {group.items.map(p => <li key={p.id} className="flex items-center gap-3 text-sm" data-id="element-311" data-mp="gdvzr356bcb4">
                      <span className="h-5 w-2 rounded-full" style={{
                  backgroundColor: p.color
                }} aria-hidden="true" data-id="element-312" data-mp="1k9cqej4c2n9" />
                      <span className="flex-1" data-id="element-313" data-mp="6jvp6lr4hev0">{p.name}</span>
                      <span className="font-mono text-xs text-muted-foreground" data-id="element-314" data-mp="u8ezf6tqg1pq">{p.dose}</span>
                    </li>)}
                </ul>
              </section>)}
          </div>

          <div className="grid gap-6 md:grid-cols-2" data-id="element-315" data-mp="kh1317x2vi0t">
            <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-316" data-mp="pgdphrk1en2v">
              <h2 className="flex items-center gap-2 font-medium" data-id="element-317" data-mp="1d67wvtll7jfq">
                <MapPin className="size-4 text-[#9b8cf0]" data-id="element-318" data-mp="1d4wx04v9ivtw" /> Delivery
              </h2>
              <p className="mt-3 text-sm" data-id="element-319" data-mp="1hyixkyo5j99c">Villa 12, Al Barsha 2, Dubai</p>
              <p className="mt-1 text-sm text-muted-foreground" data-id="element-320" data-mp="3qf26okr2jb9x">Monthly · Arrives by {arrival}</p>
            </section>
            <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-321" data-mp="3ldctaygdilio">
              <h2 className="flex items-center gap-2 font-medium" data-id="element-322" data-mp="3ggmesfk7vxzd">
                <ShieldCheck className="size-4 text-[#9b8cf0]" data-id="element-323" data-mp="3bh9spx166uxu" /> Doctor verification
              </h2>
              <p className="mt-3 text-sm" data-id="element-324" data-mp="cznovou3hfcz">{labReport.reviewer}, Family medicine</p>
              <p className="mt-1 text-sm text-muted-foreground" data-id="element-325" data-mp="801gbsp7onai">Reviews your selection against your results before packing.</p>
            </section>
          </div>
        </div>

        <aside className="h-fit rounded-2xl border border-[#9b8cf0]/40 bg-[#9b8cf0]/[0.05] p-6 lg:sticky lg:top-24" data-id="element-326" data-mp="3787otqmgut5">
          <p className="font-mono text-xs uppercase tracking-widest text-[#c4baff]" data-id="element-327" data-mp="vplyi9djjgiq">Order summary</p>
          <dl className="mt-5 space-y-3 text-sm" data-id="element-328" data-mp="qyvtwycxvhp3">
            <div className="flex justify-between" data-id="element-329" data-mp="qy3ypon97lzc"><dt className="text-muted-foreground" data-id="element-330" data-mp="qx2iz6vambba">Supplements</dt><dd data-id="element-331" data-mp="z8utghyy740q">{packPills.length}</dd></div>
            <div className="flex justify-between" data-id="element-332" data-mp="z9w62qvm3ykk"><dt className="text-muted-foreground" data-id="element-333" data-mp="138d3ujt57wpb">Duration</dt><dd data-id="element-334" data-mp="13b7oocg2jjj9">30 days</dd></div>
            <div className="flex justify-between" data-id="element-335" data-mp="187omjyd2lc9j"><dt className="text-muted-foreground" data-id="element-336" data-mp="187g4zt605is0">Delivery</dt><dd data-id="element-337" data-mp="1d717ueoamba0">Free</dd></div>
            <div className="flex justify-between border-t border-white/10 pt-3 text-base font-medium" data-id="element-338" data-mp="1d8cagb605czj">
              <dt data-id="element-339" data-mp="1i5ckex5z2zvx">Total / month</dt><dd data-id="element-340" data-mp="1i5vy21olb245">{formatAED(packPrice)}</dd>
            </div>
          </dl>
          <Button size="lg" disabled={loading} onClick={confirm} className="mt-6 w-full bg-[#9b8cf0] text-[#0f1017] hover:bg-[#b0a4f5]" data-id="element-341" data-mp="1m6xs843ngnz9">
            {loading ? <Loader2 className="size-4 animate-spin" data-id="element-342" data-mp="1vwhbhav9yjun" /> : 'Confirm & send my pack'}
          </Button>
          <Button variant="ghost" className="mt-2 w-full" onClick={() => navigate('/plan')} data-id="element-343" data-mp="20y6k6jv0ylfk">
            Edit pack
          </Button>
          <p className="mt-4 text-center text-xs text-muted-foreground" data-id="element-344" data-mp="25tlxnap4efbg">Pause or cancel anytime before the next shipment.</p>
        </aside>
      </div>
    </div>;
}